import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable, Column } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MaturityAnalysisChart } from '@/components/charts/MaturityAnalysisChart';
import {
  loanAnalyticsService,
  LoanWAL,
  LoanRiskMetrics,
  LoanSpreadBreakdown,
} from '@/services/loan-analytics.service';
import { toast } from 'react-hot-toast';
import {
  Activity,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Download,
  ChevronRight,
  Shield,
  Target,
  Layers,
  Search,
  Calendar,
  X,
} from 'lucide-react';
import { LOAN_STATUSES } from '@/constants/loan-filters';
import { formatCurrency, getStatusColor } from '@/utils/helpers';

type TabType = 'wal' | 'risk' | 'spread' | 'maturity';

const LoanAnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('risk');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [walData, setWalData] = useState<LoanWAL[]>([]);
  const [riskData, setRiskData] = useState<LoanRiskMetrics[]>([]);
  const [riskPortfolioSummary, setRiskPortfolioSummary] = useState<{
    total_loans?: number;
    total_balance?: number;
    weighted_average_ltv: number;
    weighted_average_dscr: number;
    loans_with_ltv?: number;
    loans_with_dscr?: number;
  } | null>(null);
  const [spreadData, setSpreadData] = useState<LoanSpreadBreakdown[]>([]);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const [walRes, riskRes, spreadRes] = await Promise.allSettled([
        loanAnalyticsService.getLoanWAL(),
        loanAnalyticsService.getLoanRiskMetrics(),
        loanAnalyticsService.getLoanSpreadBreakdown(),
      ]);

      if (walRes.status === 'fulfilled') {
        setWalData(walRes.value || []);
      } else {
        console.error('Error loading WAL data:', walRes.reason);
        setWalData([]);
      }

      if (riskRes.status === 'fulfilled') {
        try {
          const riskResponse = riskRes.value;
          console.log('Risk metrics response:', riskResponse);
          // Handle both old format (array) and new format (object with loans)
          if (Array.isArray(riskResponse)) {
            // Old format - direct array
            console.log('Using old format (array)');
            setRiskData(riskResponse || []);
            setRiskPortfolioSummary(null);
          } else if (
            riskResponse &&
            typeof riskResponse === 'object' &&
            'loans' in riskResponse
          ) {
            // New format - object with loans and portfolio_summary
            console.log('Using new format (object with loans)');
            const loans = Array.isArray(riskResponse.loans)
              ? riskResponse.loans
              : [];
            const portfolioSummary = riskResponse.portfolio_summary || null;
            console.log('Loans count:', loans.length);
            console.log('Portfolio summary:', portfolioSummary);
            setRiskData(loans);
            setRiskPortfolioSummary(portfolioSummary);
          } else {
            console.warn(
              'Unexpected risk metrics response format:',
              riskResponse
            );
            setRiskData([]);
            setRiskPortfolioSummary(null);
          }
        } catch (error) {
          console.error('Error processing risk metrics response:', error);
          setRiskData([]);
          setRiskPortfolioSummary(null);
        }
      } else {
        console.error('Error loading risk metrics:', riskRes.reason);
        setRiskData([]);
        setRiskPortfolioSummary(null);
      }

      if (spreadRes.status === 'fulfilled') {
        setSpreadData(spreadRes.value?.items || []);
      } else {
        console.error('Error loading spread breakdown:', spreadRes.reason);
        setSpreadData([]);
      }

      if (
        walRes.status === 'rejected' ||
        riskRes.status === 'rejected' ||
        spreadRes.status === 'rejected'
      ) {
        toast.error('Some analytics failed to load');
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
      toast.error('Failed to load analytics data');
      // Set empty arrays to prevent rendering errors
      setWalData([]);
      setRiskData([]);
      setRiskPortfolioSummary(null);
      setSpreadData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search query and status
  const filterData = <
    T extends {
      loan_name?: string;
      client_loan_number?: string;
      loan_id?: number;
    },
  >(
    data: T[]
  ): T[] => {
    let filtered = data;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const itemWithSponsor = item as T & { sponsor_borrower?: string };
        return (
          item.loan_name?.toLowerCase().includes(query) ||
          item.client_loan_number?.toLowerCase().includes(query) ||
          String(item.loan_id || '').includes(query) ||
          itemWithSponsor.sponsor_borrower?.toLowerCase().includes(query)
        );
      });
    }

    // Apply status filter for data that has loan_status field
    if (statusFilter !== 'all' && filtered.length > 0) {
      type ItemWithStatus = T & { loan_status?: string };
      const firstItem = filtered[0] as ItemWithStatus;
      if ('loan_status' in firstItem && firstItem.loan_status !== undefined) {
        filtered = filtered.filter((item) => {
          const itemWithStatus = item as ItemWithStatus;
          return itemWithStatus.loan_status === statusFilter;
        });
      }
    }

    return filtered;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';

  // Use global formatCurrency from helpers (defaults to USD for aggregated data)
  const formatCurrencyLocal = (value: number) => {
    return formatCurrency(value, 0, 'USD');
  };

  const formatPercentage = (value: number, decimals = 2) => {
    return `${(value * 100).toFixed(decimals)}%`;
  };

  const formatBasisPoints = (bps: number) => {
    const roundedBps = Math.round(bps);
    return `${roundedBps} bps`;
  };

  // WAL Columns
  const walColumns: Column<LoanWAL>[] = [
    {
      header: 'Loan Name',
      accessor: 'loan_name',
    },
    {
      header: 'Client Loan #',
      accessor: 'client_loan_number',
    },
    {
      header: 'WAL (Years)',
      accessor: (row) =>
        row.wal_years != null ? row.wal_years.toFixed(2) : 'N/A',
    },
    {
      header: 'Remaining Term',
      accessor: (row) =>
        row.remaining_term_years != null
          ? `${row.remaining_term_years.toFixed(1)} years`
          : 'N/A',
    },
    {
      header: 'Avg Life (Months)',
      accessor: (row) =>
        row.average_life_months != null
          ? row.average_life_months.toFixed(0)
          : 'N/A',
    },
    {
      header: 'Maturity Date',
      accessor: (row) =>
        new Date(row.effective_maturity_date).toLocaleDateString(),
    },
  ];

  // Risk Metrics Columns
  const riskColumns: Column<LoanRiskMetrics>[] = [
    {
      header: 'Loan Name',
      accessor: 'loan_name',
    },
    {
      header: 'Loan ID',
      accessor: (row) => row.loan_id || 'N/A',
    },
    {
      header: 'Status',
      accessor: (row) => (
        <span
          data-test-id="loan-status-badge"
          className={`px-2 py-1 text-xs rounded-full ${
            row.loan_status === 'Performing'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.loan_status}
        </span>
      ),
    },
    {
      header: 'Current Balance',
      accessor: (row) => formatCurrencyLocal(row.current_balance),
    },
    {
      header: 'LTV',
      accessor: (row) =>
        row.ltv_current != null ? formatPercentage(row.ltv_current) : 'N/A',
    },
    {
      header: 'DSCR',
      accessor: (row) =>
        row.dscr_current != null ? row.dscr_current.toFixed(2) : 'N/A',
    },
    {
      header: 'PD',
      accessor: (row) => (row.pd != null ? formatPercentage(row.pd) : 'N/A'),
    },
    {
      header: 'LGD',
      accessor: (row) => (row.lgd != null ? formatPercentage(row.lgd) : 'N/A'),
    },
    {
      header: 'Expected Loss',
      accessor: (row) =>
        row.expected_loss != null
          ? formatCurrencyLocal(row.expected_loss)
          : 'N/A',
    },
    {
      header: 'Actions',
      accessor: () => (
        <button
          data-test-id="view-loan-details-button"
          className="text-blue-600 hover:text-blue-900 cursor-not-allowed opacity-50"
          disabled
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      ),
    },
  ];

  // Spread Breakdown Columns
  const spreadColumns: Column<LoanSpreadBreakdown>[] = [
    {
      header: 'Loan Name',
      accessor: 'loan_name',
    },
    {
      header: 'Loan Number',
      accessor: (row) => row.client_loan_number || 'N/A',
    },
    {
      header: 'Sector',
      accessor: (row) => row.property_sector || 'N/A',
    },
    {
      header: 'LTV',
      accessor: (row) =>
        row.ltv_current != null
          ? (row.ltv_current * 100).toFixed(1)
          : 'N/A',
    },
    {
      header: 'Base Spread',
      accessor: (row) =>
        row.base_spread_bps != null
          ? formatBasisPoints(row.base_spread_bps)
          : 'N/A',
    },
    {
      header: 'LTV Adj',
      accessor: (row) =>
        row.ltv_adjustment_bps != null
          ? formatBasisPoints(row.ltv_adjustment_bps)
          : 'N/A',
    },
    {
      header: 'DSCR Adj',
      accessor: (row) =>
        row.dscr_adjustment_bps != null
          ? formatBasisPoints(row.dscr_adjustment_bps)
          : 'N/A',
    },
    {
      header: 'Loan Status',
      accessor: (row) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${getStatusColor(row.loan_status)}`}
        >
          {row.loan_status || 'N/A'}
        </span>
      ),
    },
    {
      header: 'Total Adj',
      accessor: (row) => formatBasisPoints(row.total_rpx_adjustment_bps || 0),
    },
    {
      header: 'Effective Spread',
      accessor: (row) =>
        row.effective_spread_bps != null
          ? formatBasisPoints(row.effective_spread_bps)
          : 'N/A',
    },
  ];

  // Calculate summary statistics
  const calculateRiskSummary = () => {
    if (!riskData || riskData.length === 0) return null;

    try {
      const totalBalance = riskData.reduce(
        (sum, r) => sum + (r.current_balance || 0),
        0
      );
      // Use weighted averages from portfolio_summary instead of calculating
      const avgLTV = riskPortfolioSummary?.weighted_average_ltv || 0;
      const avgDSCR = riskPortfolioSummary?.weighted_average_dscr || 0;
      const performingCount = riskData.filter(
        (r) => r.loan_status === 'Performing'
      ).length;
      const defaultCount = riskData.filter(
        (r) => r.loan_status === 'Default'
      ).length;
      const totalExpectedLoss = riskData.reduce(
        (sum, r) => sum + (r.expected_loss || 0),
        0
      );

      return {
        totalBalance,
        avgLTV,
        avgDSCR,
        performingCount,
        defaultCount,
        totalExpectedLoss,
        defaultRate:
          riskData.length > 0 ? (defaultCount / riskData.length) * 100 : 0,
      };
    } catch (error) {
      console.error('Error calculating risk summary:', error);
      return null;
    }
  };

  const riskSummary = calculateRiskSummary();

  // Debug logging
  useEffect(() => {
    console.log('Risk data:', riskData);
    console.log('Risk portfolio summary:', riskPortfolioSummary);
    console.log('Risk summary:', riskSummary);
    console.log('Loading:', loading);
  }, [riskData, riskPortfolioSummary, riskSummary, loading]);

  if (loading) {
    return (
      <div
        data-test-id="loan-analytics-page-container"
        className="page-container"
      >
        <div data-test-id="loan-analytics-page-inner" className="page-inner">
          <div
            data-test-id="loading-analytics-container"
            className="flex items-center justify-center h-64"
          >
            <div
              data-test-id="loading-analytics-message"
              className="text-lg text-gray-600"
            >
              Loading analytics data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-test-id="loan-analytics-page-container"
      className="page-container"
    >
      <div data-test-id="loan-analytics-page-inner" className="page-inner">
        {/* Header */}
        <div data-test-id="analytics-header-container" className="mb-8">
          <div
            data-test-id="header-content"
            className="flex justify-between items-center mb-4"
          >
            <div data-test-id="header-text-section">
              <h1
                data-test-id="analytics-page-title"
                className="section-title mb-2"
              >
                Advanced Loan Analytics
              </h1>
              <p
                data-test-id="analytics-page-subtitle"
                className="section-subtitle"
              >
                Comprehensive analysis of WAL, risk metrics, spread breakdowns,
                and maturity timeline
              </p>
            </div>
            <Button
              data-test-id="export-analytics-button"
              variant="outline"
              className="flex items-center gap-2"
              disabled
            >
              <Download className="h-4 w-4" />
              Export Analytics
            </Button>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex gap-4 items-center">
            <div
              className="relative flex-1 max-w-md"
              data-test-id="analytics-search-container"
            >
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search loans by name, borrower, or loan number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-test-id="analytics-search-input"
              />
            </div>
            {activeTab === 'risk' && (
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
                data-test-id="analytics-status-filter"
              >
                <SelectTrigger
                  className="w-[200px]"
                  data-test-id="analytics-status-filter-trigger"
                >
                  <SelectValue
                    placeholder="All Statuses"
                    data-test-id="analytics-status-filter-value"
                  />
                </SelectTrigger>
                <SelectContent data-test-id="analytics-status-filter-content">
                  <SelectItem
                    value="all"
                    data-test-id="analytics-status-filter-all"
                  >
                    All Statuses
                  </SelectItem>
                  {LOAN_STATUSES.map((status, index) => (
                    <SelectItem
                      key={status}
                      value={status}
                      data-test-id={`analytics-status-filter-${index}`}
                    >
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="flex items-center gap-2"
                data-test-id="analytics-clear-filters-button"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Excel Range Info */}
          <Card className="mt-4 p-4 bg-blue-50" data-test-id="excel-range-info">
            <p className="text-sm text-blue-800">
              <strong>Excel Ranges:</strong> Risk Metrics (Sheet1!A2:Z500), WAL
              Calculations (Sheet2!B1:G300), Spread Analysis (Sheet3!D1:K250),
              Maturity Schedule (Sheet4!A1:F200)
            </p>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div
          data-test-id="analytics-tab-navigation"
          className="flex space-x-1 mb-6 border-b"
        >
          <button
            data-test-id="risk-metrics-tab"
            onClick={() => setActiveTab('risk')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'risk'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div
              data-test-id="risk-metrics-tab-content"
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              Risk Metrics
            </div>
          </button>
          <button
            data-test-id="wal-analysis-tab"
            onClick={() => setActiveTab('wal')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors hidden ${
              activeTab === 'wal'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div
              data-test-id="wal-analysis-tab-content"
              className="flex items-center gap-2"
            >
              <Activity className="h-4 w-4" />
              WAL Analysis
            </div>
          </button>
          <button
            data-test-id="spread-breakdown-tab"
            onClick={() => setActiveTab('spread')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'spread'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div
              data-test-id="spread-breakdown-tab-content"
              className="flex items-center gap-2"
            >
              <Layers className="h-4 w-4" />
              Spread Breakdown
            </div>
          </button>
          <button
            data-test-id="maturity-analysis-tab"
            onClick={() => setActiveTab('maturity')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'maturity'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <div
              data-test-id="maturity-analysis-tab-content"
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              Maturity Analysis
            </div>
          </button>
        </div>

        {/* Risk Metrics Tab */}
        {activeTab === 'risk' && (
          <div data-test-id="risk-metrics-tab-content">
            {/* Risk Summary Cards */}
            {riskSummary ? (
              <div
                data-test-id="risk-summary-grid"
                className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
              >
                <Card data-test-id="total-exposure-card" className="p-6">
                  <div
                    data-test-id="card-header"
                    className="flex items-center justify-between mb-2"
                  >
                    <h3
                      data-test-id="total-exposure-label"
                      className="text-sm font-medium text-gray-600"
                    >
                      Total Exposure
                    </h3>
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <p
                    data-test-id="total-exposure-value"
                    className="text-2xl font-bold"
                  >
                    {formatCurrencyLocal(riskSummary.totalBalance)}
                  </p>
                  <p
                    data-test-id="total-exposure-description"
                    className="text-sm text-gray-500 mt-1"
                  >
                    Across {riskData.length} loans
                  </p>
                </Card>

                <Card data-test-id="average-ltv-card" className="p-6">
                  <div
                    data-test-id="card-header"
                    className="flex items-center justify-between mb-2"
                  >
                    <h3
                      data-test-id="average-ltv-label"
                      className="text-sm font-medium text-gray-600"
                    >
                      Average LTV
                    </h3>
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                  <p
                    data-test-id="average-ltv-value"
                    className="text-2xl font-bold"
                  >
                    {formatPercentage(riskSummary.avgLTV, 1)}
                  </p>
                  <p
                    data-test-id="average-ltv-description"
                    className="text-sm text-gray-500 mt-1"
                  >
                    Portfolio average
                  </p>
                </Card>

                <Card data-test-id="average-dscr-card" className="p-6">
                  <div
                    data-test-id="card-header"
                    className="flex items-center justify-between mb-2"
                  >
                    <h3
                      data-test-id="average-dscr-label"
                      className="text-sm font-medium text-gray-600"
                    >
                      Average DSCR
                    </h3>
                    <BarChart3 className="h-5 w-5 text-yellow-600" />
                  </div>
                  <p
                    data-test-id="average-dscr-value"
                    className="text-2xl font-bold"
                  >
                    {riskSummary.avgDSCR.toFixed(2)}x
                  </p>
                  <p
                    data-test-id="average-dscr-description"
                    className="text-sm text-gray-500 mt-1"
                  >
                    Coverage ratio
                  </p>
                </Card>

                <Card data-test-id="expected-loss-card" className="p-6">
                  <div
                    data-test-id="card-header"
                    className="flex items-center justify-between mb-2"
                  >
                    <h3
                      data-test-id="expected-loss-label"
                      className="text-sm font-medium text-gray-600"
                    >
                      Expected Loss
                    </h3>
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <p
                    data-test-id="expected-loss-value"
                    className="text-2xl font-bold"
                  >
                    {formatCurrencyLocal(riskSummary.totalExpectedLoss)}
                  </p>
                  <p
                    data-test-id="expected-loss-description"
                    className="text-sm text-gray-500 mt-1"
                  >
                    Default rate: {riskSummary.defaultRate.toFixed(1)}%
                  </p>
                </Card>
              </div>
            ) : (
              <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800">
                  No risk summary data available. Risk data: {riskData.length}{' '}
                  loans loaded.
                  {riskPortfolioSummary && (
                    <span className="block mt-2">
                      Portfolio summary available: LTV=
                      {riskPortfolioSummary.weighted_average_ltv}, DSCR=
                      {riskPortfolioSummary.weighted_average_dscr}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Risk Metrics Table */}
            <Card data-test-id="risk-metrics-table-card" className="p-6">
              <h3
                data-test-id="risk-metrics-table-title"
                className="text-lg font-semibold mb-4"
              >
                Loan Risk Metrics
              </h3>
              <DataTable
                data-test-id="risk-metrics-table"
                columns={riskColumns}
                data={filterData(riskData || [])}
                keyExtractor={(row) => String(row.loan_id)}
                emptyMessage="No risk metrics data available"
              />
            </Card>
          </div>
        )}

        {/* WAL Analysis Tab */}
        {activeTab === 'wal' && (
          <div data-test-id="wal-analysis-tab-content" className="hidden">
            {/* WAL Summary */}
            <div
              data-test-id="wal-summary-grid"
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              <Card data-test-id="average-wal-card" className="p-6">
                <h3
                  data-test-id="average-wal-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Average WAL
                </h3>
                <p
                  data-test-id="average-wal-value"
                  className="text-2xl font-bold"
                >
                  {walData.length > 0
                    ? (
                        walData.reduce((sum, w) => sum + w.wal_years, 0) /
                        walData.length
                      ).toFixed(2)
                    : '0.00'}{' '}
                  years
                </p>
              </Card>
              <Card data-test-id="shortest-wal-card" className="p-6">
                <h3
                  data-test-id="shortest-wal-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Shortest WAL
                </h3>
                <p
                  data-test-id="shortest-wal-value"
                  className="text-2xl font-bold"
                >
                  {walData.length > 0
                    ? Math.min(...walData.map((w) => w.wal_years)).toFixed(2)
                    : '0.00'}{' '}
                  years
                </p>
              </Card>
              <Card data-test-id="longest-wal-card" className="p-6">
                <h3
                  data-test-id="longest-wal-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Longest WAL
                </h3>
                <p
                  data-test-id="longest-wal-value"
                  className="text-2xl font-bold"
                >
                  {walData.length > 0
                    ? Math.max(...walData.map((w) => w.wal_years)).toFixed(2)
                    : '0.00'}{' '}
                  years
                </p>
              </Card>
            </div>

            {/* WAL Table */}
            <Card data-test-id="wal-table-card" className="p-6">
              <h3
                data-test-id="wal-table-title"
                className="text-lg font-semibold mb-4"
              >
                Weighted Average Life Analysis
              </h3>
              <DataTable
                data-test-id="wal-table"
                columns={walColumns}
                data={filterData(walData || [])}
                keyExtractor={(row) => String(row.loan_id)}
                emptyMessage="No WAL data available"
              />
            </Card>
          </div>
        )}

        {/* Spread Breakdown Tab */}
        {activeTab === 'spread' && (
          <div data-test-id="spread-breakdown-tab-content">
            {/* Spread Summary */}
            <div
              data-test-id="spread-summary-grid"
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              <Card
                data-test-id="average-effective-spread-card"
                className="p-6"
              >
                <h3
                  data-test-id="average-effective-spread-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Average Effective Spread
                </h3>
                <p
                  data-test-id="average-effective-spread-value"
                  className="text-2xl font-bold"
                >
                  {spreadData.length > 0
                    ? Math.round(
                        spreadData.reduce(
                          (sum, s) => sum + s.effective_spread_bps,
                          0
                        ) / spreadData.length
                      )
                    : 0}{' '}
                  bps
                </p>
              </Card>
              <Card data-test-id="average-base-spread-card" className="p-6">
                <h3
                  data-test-id="average-base-spread-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Average Base Spread
                </h3>
                <p
                  data-test-id="average-base-spread-value"
                  className="text-2xl font-bold"
                >
                  {spreadData.length > 0
                    ? Math.round(
                        spreadData.reduce(
                          (sum, s) => sum + s.base_spread_bps,
                          0
                        ) / spreadData.length
                      )
                    : 0}{' '}
                  bps
                </p>
              </Card>
              <Card
                data-test-id="average-total-adjustment-card"
                className="p-6"
              >
                <h3
                  data-test-id="average-total-adjustment-label"
                  className="text-sm font-medium text-gray-600 mb-2"
                >
                  Average Total Adjustment
                </h3>
                <p
                  data-test-id="average-total-adjustment-value"
                  className="text-2xl font-bold"
                >
                  {spreadData.length > 0
                    ? Math.round(
                        spreadData.reduce(
                          (sum, s) => sum + (s.total_rpx_adjustment_bps || 0),
                          0
                        ) / spreadData.length
                      )
                    : 0}{' '}
                  bps
                </p>
              </Card>
            </div>

            {/* Spread Breakdown Table */}
            <Card data-test-id="spread-breakdown-table-card" className="p-6">
              <h3
                data-test-id="spread-breakdown-table-title"
                className="text-lg font-semibold mb-4"
              >
                Credit Spread Breakdown
              </h3>
              <DataTable
                data-test-id="spread-breakdown-table"
                columns={spreadColumns}
                data={filterData(spreadData || [])}
                keyExtractor={(row) => String(row.loan_id || '')}
                emptyMessage="No spread breakdown data available"
              />
            </Card>
          </div>
        )}

        {/* Maturity Analysis Tab */}
        {activeTab === 'maturity' && (
          <div data-test-id="maturity-analysis-tab-content">
            <MaturityAnalysisChart
              loans={(walData || []).map((wal) => {
                const riskMetric = (riskData || []).find(
                  (r) => r.loan_id === wal.loan_id
                );
                return {
                  loan_id: wal.loan_id,
                  loan_name: wal.loan_name,
                  sponsor_borrower: 'N/A',
                  current_balance: riskMetric?.current_balance || 0,
                  interest_type: 'Fixed',
                  effective_maturity_date: wal.effective_maturity_date,
                  pricing_scenario: '',
                  maturity_assumption: '',
                  client_loan_number:
                    wal.client_loan_number || String(wal.loan_id),
                  sector:
                    riskMetric?.sector || riskMetric?.property_sector || '',
                  property_type: '',
                  property_lifecycle_financing: '',
                  original_balance: 0,
                  currency: 'USD',
                  client_percentage: 100,
                  pik_balance: 0,
                  position_in_capital_stack: '',
                  amortization_type: '',
                  periodicity: '',
                  interest_day_count: '',
                  loan_status: riskMetric?.loan_status || 'Performing',
                  commentary: '',
                  fixed_rate_coupon: 0,
                  floating_rate_index: '',
                  floating_rate_margin: 0,
                  credit_spread_decimal: 0,
                  loss_scenario: null,
                  pd: riskMetric?.pd || 0,
                  ead: 0,
                  lgd: riskMetric?.lgd || 0,
                  lag_to_recovery: 0,
                  default_date: null,
                  cdr: 0,
                  ltv_current: riskMetric?.ltv_current || 0,
                  dscr_current: riskMetric?.dscr_current || 0,
                  debt_yield_current: riskMetric?.debt_yield_current || null,
                  noi: null,
                  property_appraisal_value: null,
                  property_appraisal_value_date: null,
                  origination_date: '',
                  first_payment_date: '',
                  original_maturity_date: wal.effective_maturity_date,
                  prepayment_lockout_end_date: null,
                  io_end_date: null,
                  first_extension_date: null,
                  first_extension_fee: null,
                  second_extension_date: null,
                  second_extension_fee: null,
                  third_extension_date: null,
                  third_extension_fee: null,
                  exit_fee: null,
                  property_location: '',
                  in_forbearance: 'N',
                  forbearance_start_date: null,
                  forbearance_type: null,
                  valuation_date: new Date().toISOString(),
                  interest_accrual_type: '',
                  remaining_months: wal.average_life_months || 0,
                  remaining_years: wal.remaining_term_years || 0,
                  wal_years: wal.wal_years,
                  term_bucket: '',
                  benchmark_type: '',
                  exact_rate: 0,
                  prev_rate: 0,
                  next_rate: 0,
                  prev_term: 0,
                  next_term: 0,
                  benchmark_yield: 0,
                  matrix_spread_bps: 0,
                  matched_property_type: '',
                  matched_ltv_bucket: '',
                  matched_loan_type: '',
                  rpx_total_spread_bps: 0,
                  ltv_factor_adjustment_bps: 0,
                  sasb_premium_bps: 0,
                  lifecycle_adjustment_bps: 0,
                  dscr_adjustment_bps: 0,
                  ltv_performance_adjustment_bps: 0,
                  loan_status_adjustment_bps: 0,
                  senior_tiering_adjustment_bps: 0,
                  mezzanine_adjustment_bps: 0,
                  rpx_base_spread_bps: 0,
                  adjusted_spread_bps: 0,
                  credit_spread: 0,
                  component_yield_decimal: 0,
                  component_yield_pct: 0,
                  price_clean_decimal: 0,
                  modified_duration_years: 0,
                  convexity: 0,
                  loss_scenario_formatted: '',
                  default_scenario: '',
                  coupon_description: '',
                  contract_rate: 0,
                  accrual_days: 0,
                  day_count_divisor: 0,
                  accrued_interest: 0,
                  fair_value_clean: 0,
                  fair_value_dirty: 0,
                  price_dirty_decimal: 0,
                  unrealized_gain_loss: 0,
                  price_plus_25bps: 0,
                  price_minus_25bps: 0,
                  lower_price: 0,
                  upper_price: 0,
                  lower_fv: 0,
                  upper_fv: 0,
                  dv01: 0,
                  lower_mey: 0,
                  upper_mey: 0,
                };
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanAnalyticsPage;
